import React from 'react';
import { RouteEntity } from 'Models/Entities';
import { TripDetails } from 'Modules/TripDetails/TripDetails';
import { devLog } from 'Util/_HumanWritten/ConsoleLogUtils';
import { addOnsRetainedOnRouteChange } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { FerryFilterPassengers } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Sidebar/FerryFilters/PassengerDetails/FerryFilterPassengers';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import RouteMap from 'Views/Components/RouteMap/RouteMap';
import { getOldWizardData, useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { PassengerBookingWizardData } from '../../BookingWizardData/PassengerBookingWizardData';
import { VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import { getRoutesByLocations } from 'Global/JourneyElements/Utils/getRoutesByLocations';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import classNames from 'classnames';
import { confirmModal } from 'Views/Components/Modal/ModalUtils';
import { canAccessBulkBookings } from 'Modules/BulkBooking/Permissions';
import { exitBulkBookingModeWizardData, switchToBulkBookingModeWizardData } from 'Modules/BulkBooking/Utils';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';

export function FerryFilters() {
	const { wizardData, setWizardData } = useBookingWizardDataStore();

	const { isIpad } = useDeviceDetect();
	const { locations, routes, segments, stops, isLoading } = journeyElementsStore();
	const [currentRoute, setCurrentRoute] = React.useState<RouteEntity>();
	const [minimise, setMinimise] = React.useState(isIpad);

	// TODO fix multi-stop maps - temp solution
	React.useEffect(() => {
		// Only return a route if it has matching departure and destination locations
		// OR if the departure location is before the destination location in the order of the stops on the route
		const newRoute = routes.filter(
			r =>
				(r.departureId === wizardData.fromLocationId && r.destinationId === wizardData.toLocationId) ||
				(r.stops.find(s => s.locationId === wizardData.fromLocationId)?.order ?? 0) <
					(r.stops.find(s => s.locationId === wizardData.toLocationId)?.order ?? 0),
		);

		// We only want to set the route and show the image/map if there is only one route matching the departure and destination locations
		if (newRoute.length === 1 && isNotNullOrUndefined(newRoute)) {
			setCurrentRoute(newRoute[0]);
		}
	}, [wizardData.fromLocationId, wizardData.toLocationId, routes]);

	const transactionDoesNotExists = getFerryBookingTransactionIdFromStorage() === null;
	const canAccessBulkBookingsCheck = canAccessBulkBookings() && !wizardData.bookingToEdit && transactionDoesNotExists;

	return (
		<div className="trip-filters__container">
			{canAccessBulkBookingsCheck && (
				<div className="flex justify-end">
					<Button
						className="!pr-0"
						display={Display.Text}
						sizes={Sizes.Small}
						colors={Colors.Secondary}
						onClick={() => {
							if (!wizardData.isBulkBookingFlow) {
								const newWizardData = switchToBulkBookingModeWizardData(wizardData);
								setWizardData(newWizardData);
							} else {
								confirmModal('Are you sure?', 'Any progress will be lost.')
									.then(() => {
										const newWizardData = exitBulkBookingModeWizardData(wizardData);
										setWizardData(newWizardData);
									})
									.catch();
							}
						}}
					>
						{wizardData.isBulkBookingFlow ? 'Switch to Single Booking' : 'Switch to Bulk Booking'}
					</Button>
				</div>
			)}
			<TripDetails
				hideTripTypeButtons={wizardData.isBulkBookingFlow}
				hideDateRangePicker={wizardData.isBulkBookingFlow}
				model={{
					tripTypeTabSelected: wizardData.tripType,
					fromLocation: wizardData.fromLocationId,
					toLocation: wizardData.toLocationId,
					// The day the booker picked, not the day the rail is drawn around: the two part as soon as
					// a day other than the rail's middle one is chosen, and the rail's date then named a
					// day with no sailing chosen on it.
					startDate:
						wizardData.departureTrip === false
							? (wizardData.associatedTripDateTime ?? wizardData.ticketSelectionStartDate ?? wizardData.startDate)
							: (wizardData.ticketSelectionStartDate ?? wizardData.startDate),
					endDate:
						wizardData.departureTrip === true
							? (wizardData.associatedTripDateTime ?? wizardData.ticketSelectionEndDate ?? wizardData.endDate)
							: (wizardData.ticketSelectionEndDate ?? wizardData.endDate),
					route: new RouteEntity(),
					wizardData: wizardData,
				}}
				departureTrip={wizardData.departureTrip}
				typeSelected={wizardData.tripType}
				onTabSelected={newTripType => {
					const newData = new BookingWizardData({ ...wizardData });
					newData.tripType = newTripType;
					newData.userSelectedTripType = true;
					newData.automaticallyApplyAddOnsToBothDirections = true;
					if (newData.tripType === 'one way') {
						newData.returningTicketId = '';
						newData.returningJourney = undefined;
						newData.automaticallyApplyAddOnsToBothDirections = false;
					}
					setWizardData(newData);
				}}
				onDatesChanged={(date, departureDate, updateOtherDate) => {
					if (date !== undefined) {
						const newData = new BookingWizardData({ ...wizardData });
						if (departureDate || updateOtherDate) {
							newData.startDate = date;
							newData.ticketSelectionStartDate = date;

							// Deselect tickets since the dates have changed
							newData.departureTicketId = '';
							newData.departingJourney = undefined;
							devLog('DEV0019: Deselected departing ticket');
						}
						if (!departureDate || updateOtherDate) {
							newData.endDate = date;
							newData.ticketSelectionEndDate = date;

							newData.returningTicketId = '';
							newData.returningJourney = undefined;
							devLog('DEV0020: Deselected returning ticket');
						}

						setWizardData(newData);
					}
				}}
				onUpdateRouteDetails={(fromLocation, toLocation, newRoute) => {
					const newData = new BookingWizardData({ ...wizardData });
					const oldWizardData = getOldWizardData();

					newData.fromLocationId = fromLocation;
					newData.toLocationId = toLocation;

					// We only want to revisit the add-ons if the route has changed, however this
					// function is called whenever the route is updated, even if it hasn't necessarily
					// changed. So we don't want it to update if the from and to locations are the same
					// as the old wizard data (editing a booking from the cart).
					//
					// This applies in alteration mode too now that the route can be changed while
					// editing. Add-on sections are scoped to routes and ferries
					// (RoutesAdditionalBookingSections), so an add-on is kept when the new journey
					// still offers it and cleared when it does not — see
					// addOnsRetainedOnRouteChange for the three cases. Note that clearing is not
					// undone by switching back to the original route, same as in CREATE mode.
					if (
						(newData.innerWizardData instanceof VehicleBookingWizardData ||
							newData.innerWizardData instanceof PassengerBookingWizardData) &&
						isNotNullOrUndefined(oldWizardData) &&
						(oldWizardData.fromLocationId !== fromLocation || oldWizardData.toLocationId !== toLocation)
					) {
						// The union of both legs' routes: the picker offers every matching section on
						// both legs, and a return pair's legs are on different (directional) routes.
						const journeyElements = { locations, routes, segments, stops };
						const routeIdsOnNewJourney = [
							...getRoutesByLocations(journeyElements, fromLocation, toLocation),
							...(newData.tripType === 'return'
								? getRoutesByLocations(journeyElements, toLocation, fromLocation)
								: []),
						].map(route => route.id);

						newData.innerWizardData.departingTripOptions = addOnsRetainedOnRouteChange(
							oldWizardData.innerWizardData.departingTripOptions,
							newData.wizardMode,
							routeIdsOnNewJourney,
						);
						newData.innerWizardData.returningTripOptions = addOnsRetainedOnRouteChange(
							oldWizardData.innerWizardData.returningTripOptions,
							newData.wizardMode,
							routeIdsOnNewJourney,
						);
					}

					setWizardData(newData);
				}}
			/>
			<div className="dashed-border" />
			<If condition={isIpad}>
				<If condition={isNotNullOrUndefined(currentRoute) && currentRoute.stops.length === 2}>
					<RouteMap currentRoute={currentRoute?.id} locations={locations} routes={routes} isMobile={isIpad} />
				</If>
				<If
					condition={
						isNotNullOrUndefined(currentRoute) &&
						currentRoute.stops.length !== 2 &&
						isNotNullOrUndefined(currentRoute.mapId)
					}
				>
					<div
						className={classNames('map__container', {
							'map__container--minimised': minimise && isIpad && !isLoading,
						})}
						style={{ display: isLoading ? 'none' : undefined }}
					>
						<If condition={isIpad}>
							<Button
								className="map__button"
								onClick={() => {
									setMinimise(!minimise);
								}}
								icon={{ icon: `${minimise ? 'minimise-2' : 'expand-2'}`, iconPos: 'icon-left' }}
							/>
						</If>
						<img
							className="event-detailed-view__supporting-image"
							src={`/api/files/${currentRoute?.mapId}`}
							key={currentRoute?.mapId}
							alt=""
						/>
					</div>
				</If>
			</If>
			<FerryFilterPassengers />
		</div>
	);
}
